/**
 * Финансы проекта.
 *
 * Управленческий реестр расходов, не бухгалтерия. Суммы разных валют не
 * складываются: сводка показывается по каждой валюте отдельно, и это не
 * недоделка, а прямое требование (документ «Структура интерфейса», §15).
 */

import { AlertTriangleIcon, PlusIcon, ReceiptIcon, RepeatIcon } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import type { ExpenseRowDto, FinanceSummaryDto } from '@project-os/contracts';
import {
  CURRENCIES,
  EXPENSE_CATEGORIES,
  formatMoney,
  getItemTypeDefinition,
  type Currency,
} from '@project-os/contracts';
import { useProjectMembers } from '@/api/members';
import { useFinance } from '@/api/resources';
import { useWorkstreams } from '@/api/workstreams';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { CardListSkeleton, EmptyState, ErrorState } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserAvatar } from '@/components/common/user-avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, pluralRu } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemDetailPanel } from '../items/ItemDetailPanel';
import { QuickAddDialog } from '../items/QuickAddDialog';
import { ItemTypeIcon } from '../items/item-icon';
import { useProjectContext } from '../project/use-project-context';

const ALL = '__all__';

/** Подписи категорий берутся из реестра, а не дублируются здесь. */
const CATEGORY_LABELS = new Map(
  (
    getItemTypeDefinition('EXPENSE').properties.find((p) => p.key === 'category')?.options ?? []
  ).map((option) => [option.value, option.labelRu]),
);

export function FinancePage() {
  const project = useProjectContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const currency = searchParams.get('currency') ?? undefined;
  const category = searchParams.get('category') ?? undefined;
  const includeCancelled = searchParams.get('cancelled') === 'true';

  const { data, isPending, isError, error, refetch } = useFinance(project.id, {
    currency,
    category,
    includeCancelled,
  });
  const { data: workstreams = [] } = useWorkstreams(project.id);
  const { data: members = [] } = useProjectMembers(project.id);

  const selectedId = searchParams.get('item');
  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );

  const canEdit = project.permissions.includes('ITEM_WRITE');
  const [addOpen, setAddOpen] = useState(false);

  return (
    <>
      <PageHeader
        title="Финансы"
        description="Управленческий реестр расходов. Суммы разных валют не складываются."
        actions={
          canEdit ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <PlusIcon />
              Добавить расход
            </Button>
          ) : null
        }
      />

      <PageBody className="space-y-4">
        {isPending ? (
          <CardListSkeleton count={3} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            {data.summary.length === 0 ? null : (
              <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
                {data.summary.map((summary) => (
                  <CurrencySummaryCard key={summary.currency} summary={summary} />
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={currency ?? ALL}
                onValueChange={(value) => setParam('currency', value === ALL ? null : value)}
              >
                <SelectTrigger size="sm" className="w-36">
                  <SelectValue placeholder="Валюта" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Все валюты</SelectItem>
                  {CURRENCIES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={category ?? ALL}
                onValueChange={(value) => setParam('category', value === ALL ? null : value)}
              >
                <SelectTrigger size="sm" className="w-56">
                  <SelectValue placeholder="Категория" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Все категории</SelectItem>
                  {EXPENSE_CATEGORIES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {CATEGORY_LABELS.get(code) ?? code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant={includeCancelled ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8"
                onClick={() => setParam('cancelled', includeCancelled ? null : 'true')}
              >
                Отменённые
              </Button>
            </div>

            {data.expenses.length === 0 ? (
              <EmptyState
                icon={<ReceiptIcon />}
                title={currency || category ? 'Под фильтры ничего не подошло' : 'Расходов пока нет'}
                description={
                  currency || category
                    ? 'Ни один расход не подходит под выбранные валюту и категорию.'
                    : getItemTypeDefinition('EXPENSE').emptyStateRu
                }
                action={
                  canEdit && !currency && !category ? (
                    // Форма открывается здесь же.
                    //
                    // Кнопка была ссылкой на «Элементы» с фильтром по типу:
                    // человек нажимал «Добавить расход» и оказывался на другой
                    // странице, где расход всё ещё нужно было добавлять. Это
                    // читалось как сбой, а не как переход, — и справедливо:
                    // кнопка обещала одно, а делала другое.
                    <Button onClick={() => setAddOpen(true)}>
                      <PlusIcon />
                      Добавить расход
                    </Button>
                  ) : null
                }
                secondaryAction={
                  currency || category ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setParam('currency', null);
                        setParam('category', null);
                      }}
                    >
                      Сбросить фильтры
                    </Button>
                  ) : null
                }
                hint="Суммы разных валют не складываются: курса в системе нет, и общий итог был бы выдуманным числом."
              />
            ) : (
              <>
                <ExpensesTable rows={data.expenses} onOpen={(id) => setParam('item', id)} />

                {data.byCategory.length > 1 ? (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">По категориям</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {data.byCategory.map((bucket) => (
                          <li
                            key={`${bucket.currency}:${bucket.category ?? ''}`}
                            className="flex items-center gap-3 text-sm"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {bucket.categoryLabelRu}
                            </span>
                            <span className="text-2xs text-muted-foreground tabular-nums">
                              {pluralRu(bucket.count, ['расход', 'расхода', 'расходов'])}
                            </span>
                            <span className="w-32 text-right tabular-nums">
                              {formatMoney({
                                amountMinor: bucket.amountMinor,
                                currency: bucket.currency,
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ) : null}
              </>
            )}
          </>
        )}
      </PageBody>

      <QuickAddDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={project.id}
        defaultType="EXPENSE"
        // Созданный расход сразу открывается карточкой: у расхода есть сумма,
        // валюта и дата, которых в быстрой форме нет.
        onCreated={(id) => setParam('item', id)}
      />

      <ItemDetailPanel
        projectId={project.id}
        itemId={selectedId}
        onClose={() => setParam('item', null)}
        onOpenItem={(id) => setParam('item', id)}
        canEdit={canEdit}
        canReview={project.permissions.includes('REVIEW_WRITE')}
        workstreams={workstreams}
        members={members.map((member) => member.user)}
      />
    </>
  );
}

function CurrencySummaryCard({ summary }: { summary: FinanceSummaryDto }) {
  const money = (amountMinor: number) =>
    formatMoney({ amountMinor, currency: summary.currency as Currency });

  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="flex items-center gap-2 text-sm">
          <RepeatIcon className="size-4 text-muted-foreground" />
          {summary.currency}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2">
          <SummaryRow
            label="Периодические, в месяц"
            value={money(summary.recurringMonthly)}
            hint={pluralRu(summary.recurringCount, ['расход', 'расхода', 'расходов'])}
          />
          <SummaryRow
            label="Разовые в этом месяце"
            value={money(summary.oneTimeThisMonth)}
            hint={pluralRu(summary.oneTimeThisMonthCount, ['расход', 'расхода', 'расходов'])}
          />
          <SummaryRow
            label="Платежи в ближайшие 30 дней"
            value={money(summary.upcoming)}
            hint={pluralRu(summary.upcomingCount, ['платёж', 'платежа', 'платежей'])}
          />
          <SummaryRow
            label="Без ответственного"
            value={money(summary.withoutOwner)}
            hint={pluralRu(summary.withoutOwnerCount, ['расход', 'расхода', 'расходов'])}
            warn={summary.withoutOwnerCount > 0}
          />
        </dl>
      </CardContent>
    </Card>
  );
}

function SummaryRow({
  label,
  value,
  hint,
  warn,
}: {
  label: string;
  value: string;
  hint: string;
  warn?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{label}</dt>
      <dd className="flex items-baseline gap-1.5">
        <span className={cn('text-sm font-medium tabular-nums', warn && 'text-tone-warning')}>
          {value}
        </span>
        <span className="text-2xs text-muted-foreground">{hint}</span>
      </dd>
    </div>
  );
}

function ExpensesTable({
  rows,
  onOpen,
}: {
  rows: ExpenseRowDto[];
  onOpen: (itemId: string) => void;
}) {
  return (
    <Card className="py-0">
      <CardContent className="px-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24 pl-4">Дата</TableHead>
              <TableHead>Название</TableHead>
              <TableHead className="hidden w-32 2xl:table-cell">Поставщик</TableHead>
              <TableHead className="hidden w-40 lg:table-cell">Категория</TableHead>
              <TableHead className="w-28 text-right">Сумма</TableHead>
              <TableHead className="hidden w-28 lg:table-cell">Периодичность</TableHead>
              <TableHead className="w-28">Платёж</TableHead>
              <TableHead className="hidden w-32 lg:table-cell">Ответственный</TableHead>
              <TableHead className="hidden w-32 2xl:table-cell">Инструмент</TableHead>
              <TableHead className="w-24 pr-4">Оплата</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.item.id}
                className="cursor-pointer"
                onClick={() => onOpen(row.item.id)}
              >
                <TableCell className="pl-4 text-xs text-muted-foreground tabular-nums">
                  {formatDate(row.expenseDate)}
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1.5">
                    <ItemTypeIcon type="EXPENSE" className="size-3.5 shrink-0" />
                    <span className="min-w-0 truncate font-medium">{row.item.title}</span>
                  </span>
                </TableCell>
                <TableCell className="hidden text-xs text-muted-foreground 2xl:table-cell">
                  <span className="block truncate">{row.vendor ?? '—'}</span>
                </TableCell>
                <TableCell className="hidden text-xs lg:table-cell">
                  <span className="block truncate">{row.categoryLabelRu ?? '—'}</span>
                </TableCell>
                <TableCell className="text-right text-sm font-medium tabular-nums">
                  {formatMoney({ amountMinor: row.amountMinor, currency: row.currency })}
                </TableCell>
                <TableCell className="hidden text-xs lg:table-cell">
                  {row.recurrenceLabelRu}
                </TableCell>
                <TableCell
                  className={cn(
                    'text-xs tabular-nums',
                    row.isOverdue ? 'text-tone-danger' : 'text-muted-foreground',
                  )}
                >
                  {row.nextPaymentDate ? (
                    <span className="flex items-center gap-1">
                      {row.isOverdue ? <AlertTriangleIcon className="size-3" /> : null}
                      {formatDate(row.nextPaymentDate)}
                    </span>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="hidden text-xs lg:table-cell">
                  {row.item.owner ? (
                    <span className="flex items-center gap-1.5">
                      <UserAvatar user={row.item.owner} size="sm" />
                      <span className="truncate">{row.item.owner.name}</span>
                    </span>
                  ) : (
                    <span className="text-tone-warning">не назначен</span>
                  )}
                </TableCell>
                <TableCell className="hidden text-xs 2xl:table-cell">
                  {row.linkedTool ? (
                    <span className="flex items-center gap-1">
                      <ItemTypeIcon type="TOOL" className="size-3 shrink-0" />
                      <span className="truncate">{row.linkedTool.title}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground/70">—</span>
                  )}
                </TableCell>
                <TableCell className="pr-4">
                  {row.item.status === 'CANCELLED' ? (
                    <ItemStatusBadge type="EXPENSE" status={row.item.status} />
                  ) : (
                    <span
                      className={cn(
                        'text-xs',
                        row.isOverdue ? 'text-tone-danger' : 'text-muted-foreground',
                      )}
                    >
                      {row.paymentStatusLabelRu ?? '—'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
